// src/components/Campaign/ListManager.tsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Chip,
} from "@mui/material";
import Papa from "papaparse";
import { CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";
import BusinessSettingsContext from "../Context/Settings/BusinessSettingsContext";
import {
  createList,
  getLists,
  importContactsCsv,
  getImport,
  getListMembers,
  ContactList,
  ImportJob,
  ListMember,
} from "../../services/campaign.service";

type Props = { businessId: string };

type ParsedCsv = {
  headers: string[];
  rows: Record<string, string>[];
  sampleRows: Record<string, string>[];
  rawText: string;
};

function guessColumn(headers: string[], candidates: string[]) {
  const lower = headers.map((h) => h.toLowerCase().trim());
  for (const c of candidates) {
    const idx = lower.indexOf(c.toLowerCase());
    if (idx >= 0) return headers[idx];
  }
  for (const h of headers) {
    const hl = h.toLowerCase();
    if (candidates.some((c) => hl.includes(c.toLowerCase()))) return h;
  }
  return "";
}

const countryOptions: { code: CountryCode; label: string }[] = [
  { code: "IN", label: "India (+91)" },
  { code: "US", label: "United States (+1)" },
  { code: "GB", label: "United Kingdom (+44)" },
  { code: "AE", label: "United Arab Emirates (+971)" },
  { code: "SG", label: "Singapore (+65)" },
  { code: "AU", label: "Australia (+61)" },
  { code: "CA", label: "Canada (+1)" },
];

function normalizePhone(raw: string, defaultCountry: CountryCode) {
  const original = (raw || "").trim();
  if (!original) return { original, phone: "", ok: false, changed: false };
  const parsedPhone = parsePhoneNumberFromString(original, defaultCountry);
  const ok = Boolean(parsedPhone?.isValid());
  const phone = ok ? parsedPhone!.number : original;
  return { original, phone, ok, changed: ok && phone !== original };
}

export default function ListManager({ businessId }: Props) {
  const { settings } = useContext(BusinessSettingsContext);

  // NOTE: uuid is businessId for campaign feature
  const uuid = businessId || settings?.businessId;

  const [lists, setLists] = useState<ContactList[]>([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedListId, setSelectedListId] = useState("");

  const [importId, setImportId] = useState("");
  const [importStatus, setImportStatus] = useState<ImportJob | null>(null);

  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const pollTimer = useRef<number | null>(null);

  // CSV state
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [phoneCol, setPhoneCol] = useState("");
  const [nameCol, setNameCol] = useState(""); // optional
  const [defaultCountry, setDefaultCountry] = useState<CountryCode>("IN");

  // ---- Members UI ----
  const [membersOpen, setMembersOpen] = useState(false);
  const [members, setMembers] = useState<ListMember[]>([]);
  const [membersPage, setMembersPage] = useState(1);
  const [membersLimit] = useState(50);
  const [membersBusy, setMembersBusy] = useState(false);
  const [membersErr, setMembersErr] = useState("");
  const [memberSearch, setMemberSearch] = useState("");

  const load = async (u: string, preferredListId?: string) => {
    const data = await getLists(u);
    setLists(data);
    setSelectedListId((prev) => preferredListId || prev);
  };

  useEffect(() => {
    if (!uuid) return;

    load(uuid).catch((e: any) => {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to load lists (check API baseURL)",
      );
    });

    return () => {
      if (pollTimer.current) window.clearInterval(pollTimer.current);
    };
  }, [uuid]);

  // When list changes, hide members view
  useEffect(() => {
    setMembersOpen(false);
    setMembers([]);
    setMembersPage(1);
    setMemberSearch("");
    setMembersErr("");
  }, [selectedListId]);

  const onCreate = async () => {
    if (!uuid) return;
    setErr("");
    setInfo("");
    setBusy(true);
    try {
      const created = await createList(uuid, name, desc);
      setName("");
      setDesc("");
      await load(uuid, created.id);
      setInfo("List created.");
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail || e?.message || "Failed to create list",
      );
    } finally {
      setBusy(false);
    }
  };

  const poll = (u: string, id: string) => {
    if (pollTimer.current) window.clearInterval(pollTimer.current);

    pollTimer.current = window.setInterval(async () => {
      try {
        const st = await getImport(u, id);
        setImportStatus(st);
        if (st.status === "DONE" || st.status === "FAILED") {
          if (pollTimer.current) window.clearInterval(pollTimer.current);
        }
      } catch {
        // ignore transient polling errors
      }
    }, 1200);
  };

  const parseCsvFile = async (file: File) => {
    const text = await file.text();
    if (!text.trim())
      throw new Error("CSV is empty. Please re-export as CSV UTF-8.");

    return new Promise<ParsedCsv>((resolve, reject) => {
      Papa.parse<Record<string, any>>(text, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        complete: (results: any) => {
          const headers = (results.meta.fields || []).filter(
            Boolean,
          ) as string[];
          const rowsRaw = (results.data || []) as Record<string, any>[];

          const rows = rowsRaw.map((r) => {
            const out: Record<string, string> = {};
            headers.forEach((h) => {
              const v = r?.[h];
              out[h] = v === undefined || v === null ? "" : String(v);
            });
            return out;
          });

          resolve({
            headers,
            rows,
            sampleRows: rows.slice(0, 20),
            rawText: text,
          });
        },
        error: (error: any) => reject(error),
      });
    });
  };

  const onPickFile = async (file: File | null) => {
    setErr("");
    setInfo("");
    setImportId("");
    setImportStatus(null);

    setParsed(null);
    setPhoneCol("");
    setNameCol("");

    setCsvFile(file);

    if (!file) return;

    try {
      setBusy(true);
      setInfo("Reading CSV...");
      const p = await parseCsvFile(file);
      if (!p.headers.length)
        throw new Error("No headers found. Ensure first row is header.");

      setParsed(p);

      const guessedPhone = guessColumn(p.headers, [
        "phone",
        "phonenumber",
        "mobile",
        "number",
        "contact",
      ]);
      const guessedName = guessColumn(p.headers, [
        "name",
        "customer",
        "fullname",
      ]);

      setPhoneCol(guessedPhone || p.headers[0]);
      setNameCol(guessedName || "");

      setInfo(`Loaded ${p.rows.length} rows. Map columns and click Import.`);
    } catch (e: any) {
      setErr(e?.message || "Failed to parse CSV");
    } finally {
      setBusy(false);
    }
  };

  const mappedPreview = useMemo(() => {
    if (!parsed || !phoneCol) return { rows: [], invalidCount: 0, validCount: 0, normalizedCount: 0 };
    const allPhones = parsed.rows.map((r) => normalizePhone(r[phoneCol] || "", defaultCountry));
    const rows = parsed.sampleRows.map((r) => {
      const nm = nameCol ? (r[nameCol] || "").trim() : "";
      return { ...normalizePhone(r[phoneCol] || "", defaultCountry), name: nm };
    });
    return {
      rows,
      invalidCount: allPhones.filter((phone) => !phone.ok).length,
      validCount: allPhones.filter((phone) => phone.ok).length,
      normalizedCount: allPhones.filter((phone) => phone.changed).length,
    };
  }, [parsed, phoneCol, nameCol, defaultCountry]);

  const buildNormalizedCsvFile = () => {
    if (!parsed || !phoneCol) return null;

    const normalized = parsed.rows.map((r) => {
      const phone = normalizePhone(r[phoneCol] || "", defaultCountry).phone;
      const nm = nameCol ? (r[nameCol] || "").trim() : "";
      const additionalColumns = Object.fromEntries(
        parsed.headers
          .filter((header) => header !== phoneCol && !["phone", "name"].includes(header.trim().toLowerCase()))
          .map((header) => [header, (r[header] || "").trim()]),
      );
      return { phone, name: nm, ...additionalColumns };
    });

    const csv = Papa.unparse(normalized, {
      skipEmptyLines: true,
    });

    return new File([csv], csvFile?.name || "imported-contacts.csv", {
      type: "text/csv",
    });
  };

  const onImport = async () => {
    if (!uuid) return;
    setErr("");
    setInfo("");
    setImportStatus(null);

    if (!csvFile || !parsed) return setErr("Please choose a CSV file first.");
    if (!phoneCol) return setErr("Please select the Phone column.");

    if (
      mappedPreview.invalidCount === parsed.rows.length &&
      parsed.rows.length > 0
    ) {
      return setErr(
        "No valid phone numbers were found. Check the phone column or choose the correct default country.",
      );
    }

    const normalizedFile = buildNormalizedCsvFile();
    if (!normalizedFile) return setErr("Unable to generate normalized CSV.");

    setBusy(true);
    try {
      setInfo(
        selectedListId
          ? "Uploading contacts to the selected list..."
          : "Creating a list from the CSV filename and importing contacts...",
      );
      const r = await importContactsCsv(
        uuid,
        selectedListId || undefined,
        normalizedFile,
        defaultCountry,
      );

      setImportId(r.importId);
      await load(uuid, r.listId);
      setInfo(
        r.listCreated
          ? `Created list "${r.listName}". Import started and status is being tracked.`
          : `Import into "${r.listName}" started. Tracking status...`,
      );
      poll(uuid, r.importId);
    } catch (e: any) {
      const status = e?.response?.status;
      const data = e?.response?.data;

      setErr(
        `Import failed${status ? ` (HTTP ${status})` : ""}: ` +
          (data?.detail
            ? data.detail
            : typeof data === "string"
              ? data
              : data
                ? JSON.stringify(data)
                : e?.message || "Unknown error"),
      );
    } finally {
      setBusy(false);
    }
  };

  const loadMembers = async (page = 1) => {
    if (!uuid || !selectedListId) return;

    setMembersErr("");
    setMembersBusy(true);
    try {
      const data = await getListMembers(
        uuid,
        selectedListId,
        page,
        membersLimit,
      );
      setMembers(data || []);
      setMembersPage(page);
      setMembersOpen(true);
    } catch (e: any) {
      setMembersErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to load list members",
      );
    } finally {
      setMembersBusy(false);
    }
  };

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const phone = (m.phoneE164 || "").toLowerCase();
      const nm = (m.name || "").toLowerCase();
      const attributeValues = Object.values(m.attributes || {}).join(" ").toLowerCase();
      return phone.includes(q) || nm.includes(q) || attributeValues.includes(q);
    });
  }, [members, memberSearch]);

  const memberColumns = useMemo(
    () => Array.from(new Set(members.flatMap((member) => Object.keys(member.attributes || {})))),
    [members],
  );

  if (!uuid) return <Alert severity="warning">Loading settings...</Alert>;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "divider", minWidth: 0 }}>
        <Typography component="h2" variant="h6">Contact lists</Typography>

        {err && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err}
          </Alert>
        )}
        {info && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {info}
          </Alert>
        )}

        {/* Create list */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mt: 2 }}
        >
          <TextField
            label="List name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={onCreate}
            disabled={busy || !name.trim()}
            startIcon={busy ? <CircularProgress size={18} /> : undefined}
          >
            Create
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* List select + actions */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <FormControl fullWidth>
            <InputLabel>Import destination</InputLabel>
            <Select
            label="Import destination"
            value={selectedListId}
            onChange={(e) => setSelectedListId(e.target.value)}
          >
            <MenuItem value="">New list — use CSV filename</MenuItem>
            {lists.map((l) => (
              <MenuItem key={l._id} value={l._id}>
                {l.name}
              </MenuItem>
            ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
          >
            {csvFile ? "Change CSV" : "Upload CSV"}
          </Button>

          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept=".csv,text/csv"
            onChange={async (e) => {
              const file = e.target.files?.[0] || null;
              await onPickFile(file);
              e.currentTarget.value = "";
            }}
          />

          <Button
            variant="contained"
            onClick={onImport}
            disabled={busy || !parsed || !phoneCol}
            startIcon={busy ? <CircularProgress size={18} /> : undefined}
          >
            Import
          </Button>

          <Button
            variant="outlined"
            onClick={() => loadMembers(1)}
            disabled={!selectedListId || membersBusy || busy}
          >
            {membersBusy ? "Loading..." : "View Contacts"}
          </Button>
        </Stack>

        {/* Selected file */}
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, opacity: 0.8 }}
        >
          Selected file:{" "}
          {csvFile
            ? `${csvFile.name} (${Math.round(csvFile.size / 1024)} KB)`
            : "None"}
        </Typography>
        {csvFile && !selectedListId && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 0.5, color: "primary.main" }}
          >
            Import will create a new list named "
            {csvFile.name.replace(/\.csv$/i, "") || "Imported contacts"}".
          </Typography>
        )}

        {/* Mapping UI */}
        {parsed && (
          <>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <FormControl fullWidth>
                <InputLabel>Phone column (required)</InputLabel>
                <Select
                  value={phoneCol}
                  label="Phone column (required)"
                  onChange={(e) => setPhoneCol(e.target.value)}
                >
                  {parsed.headers.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Name column (optional)</InputLabel>
                <Select
                  value={nameCol}
                  label="Name column (optional)"
                  onChange={(e) => setNameCol(e.target.value)}
                >
                  <MenuItem value="">(none)</MenuItem>
                  {parsed.headers.map((h) => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Default country</InputLabel>
                <Select
                  value={defaultCountry}
                  label="Default country"
                  onChange={(e) => setDefaultCountry(e.target.value as CountryCode)}
                >
                  {countryOptions.map((country) => (
                    <MenuItem key={country.code} value={country.code}>{country.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Alert severity="info" sx={{ mt: 2 }}>
              Local numbers will use the selected country. Existing international numbers are preserved, and invalid rows are skipped during import.
            </Alert>

            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
              <Chip label={`Rows: ${parsed.rows.length}`} />
              <Chip
                color="success"
                label={`Valid: ${mappedPreview.validCount}`}
              />
              <Chip
                color={mappedPreview.invalidCount ? "error" : "default"}
                label={`Invalid: ${mappedPreview.invalidCount}`}
              />
              <Chip color="info" label={`Auto-formatted: ${mappedPreview.normalizedCount}`} />
            </Stack>

            <Box sx={{ mt: 2, overflowX: "auto" }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview (first {mappedPreview.rows.length} rows after mapping)
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Original phone</TableCell>
                    <TableCell>WhatsApp number</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappedPreview.rows.map((r, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{r.original || "—"}</TableCell>
                      <TableCell>{r.ok ? r.phone : "—"}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>
                        {r.ok ? (
                          <Chip size="small" color={r.changed ? "info" : "success"} label={r.changed ? "Auto-formatted" : "Ready"} />
                        ) : (
                          <Chip size="small" color="error" label="Invalid" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}

        {/* Import status */}
        {importId && importStatus && (
          <Alert
            severity={importStatus.status === "FAILED" ? "error" : "success"}
            sx={{ mt: 2 }}
          >
            Import: {importStatus.status} | Total: {importStatus.totalRows} |
            Success: {importStatus.successCount} | Invalid:{" "}
            {importStatus.invalidCount}
          </Alert>
        )}

        {/* Members table */}
        {membersErr && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {membersErr}
          </Alert>
        )}

        {membersOpen && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Contacts in this list
              </Typography>

              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="Search"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                />
                <Button
                  variant="outlined"
                  onClick={() => loadMembers(membersPage)}
                  disabled={membersBusy}
                >
                  Refresh
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setMembersOpen(false);
                    setMembers([]);
                    setMembersPage(1);
                    setMemberSearch("");
                    setMembersErr("");
                  }}
                >
                  Hide
                </Button>
              </Stack>
            </Stack>

            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Phone</TableCell>
                    <TableCell>Name</TableCell>
                    {memberColumns.map((column) => <TableCell key={column}>{column}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMembers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2 + memberColumns.length} sx={{ opacity: 0.7 }}>
                        No contacts found on this page.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMembers.map((m) => (
                      <TableRow key={m.id}>
                      <TableCell>{m.phoneE164}</TableCell>
                      <TableCell>{m.name || "-"}</TableCell>
                      {memberColumns.map((column) => <TableCell key={column}>{m.attributes?.[column] || "-"}</TableCell>)}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                onClick={() => loadMembers(Math.max(1, membersPage - 1))}
                disabled={membersBusy || membersPage <= 1}
              >
                Prev
              </Button>
              <Chip label={`Page ${membersPage}`} />
              <Button
                variant="outlined"
                onClick={() => loadMembers(membersPage + 1)}
                disabled={membersBusy || members.length < membersLimit}
              >
                Next
              </Button>
              <Chip
                variant="outlined"
                label={`Showing ${members.length} (limit ${membersLimit})`}
              />
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
